import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Paper, Alert, Button } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "../../components/payment/CheckoutForm";
import { useNotification } from "../../contexts/NotificationContext";
import { getStripePromise, isStripeConfigured } from "../../config/stripe";

const StripeCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [clientSecret, setClientSecret] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeError, setStripeError] = useState(null);

  useEffect(() => {
    // Check if Stripe is configured
    if (!isStripeConfigured()) {
      setStripeError("Stripe payment configuration is missing. Please contact the administrator.");
      showNotification("Stripe payment configuration error", "error");
      setLoading(false);
      return;
    }

    // Parse query parameters
    const params = new URLSearchParams(location.search);
    const secret = params.get("client_secret");
    const subId = params.get("subscription_id");

    if (!secret) {
      setError("Missing payment information. Please try again.");
      setLoading(false);
      return;
    }

    setClientSecret(secret);
    if (subId) setSubscriptionId(subId);
    setLoading(false);
  }, [location, showNotification]);

  const handlePaymentSuccess = () => {
    showNotification("Payment successful! Your subscription has been activated.", "success");
    // Redirect to the subscription details page if we have an ID
    if (subscriptionId) {
      navigate(`/customers/${subscriptionId}`);
    } else {
      navigate("/subscriptions");
    }
  };

  const handlePaymentError = (errorMessage) => {
    showNotification(`Payment failed: ${errorMessage}`, "error");
  };

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#1976d2',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (stripeError) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {stripeError}
        </Alert>
        <Typography variant="body1">
          The payment system is not properly configured. Please contact the administrator.
        </Typography>
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button variant="contained" onClick={() => navigate("/subscriptions")}>
            Return to Subscriptions
          </Button>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          Please go back and try again. If the problem persists, contact support.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Complete Your Subscription Payment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please provide your payment details to activate your subscription.
        </Typography>
        
        {clientSecret && (
          <Elements stripe={getStripePromise()} options={options}>
            <CheckoutForm 
              onSuccess={handlePaymentSuccess} 
              onError={handlePaymentError}
            />
          </Elements>
        )}
      </Paper>
    </Box>
  );
};

export default StripeCheckout; 